import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import {
  MapPin, Plus, Pencil, Trash2, Phone, Mail, Building2, School, Trees, BookOpen,
  Loader2, ExternalLink, Globe, Link2
} from 'lucide-react';
import { toast } from 'sonner';

const CATEGORY_OPTIONS = [
  { value: 'independent', label: 'Independent', icon: Building2, color: 'text-gray-500', bg: 'bg-gray-100' },
  { value: 'school', label: 'School', icon: School, color: 'text-blue-600', bg: 'bg-blue-50' },
  { value: 'park_district', label: 'Park District', icon: Trees, color: 'text-green-600', bg: 'bg-green-50' },
  { value: 'library', label: 'Library', icon: BookOpen, color: 'text-purple-600', bg: 'bg-purple-50' },
];

export default function AdminLocationsPage() {
  const { api } = useAuth();
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingLocation, setEditingLocation] = useState(null);

  const [formData, setFormData] = useState({
    name: '',
    address: '',
    city: '',
    state: 'IL',
    zip_code: '',
    country: 'United States',
    country_code: 'US',
    timezone: 'America/Chicago',
    phone: '',
    email: '',
    capacity: 15,
    active: true,
    location_category: 'independent',
    eligibility: 'open',
    eligibility_detail: '',
    external_purchase: false,
    external_purchase_url: '',
  });

  useEffect(() => { fetchLocations(); }, []);

  const fetchLocations = async () => {
    try {
      const response = await api.get('/locations/?page_size=200');
      setLocations(response.data?.results || response.data || []);
    } catch { toast.error('Failed to load locations'); }
    finally { setLoading(false); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        name: formData.name,
        address: formData.address,
        city: formData.city,
        state: formData.state,
        zip_code: formData.zip_code,
        country: formData.country,
        country_code: formData.country_code,
        timezone: formData.timezone,
        phone: formData.phone || null,
        email: formData.email || null,
        capacity: formData.capacity,
        active: formData.active,
        location_category: formData.location_category,
        eligibility: formData.eligibility,
        eligibility_detail: formData.eligibility_detail,
        external_purchase: formData.external_purchase,
        external_purchase_url: formData.external_purchase ? formData.external_purchase_url : '',
      };

      if (editingLocation) {
        await api.put(`/locations/${editingLocation.id}/`, payload);
        toast.success('Location updated');
      } else {
        await api.post('/locations/', payload);
        toast.success('Location created');
      }
      setDialogOpen(false);
      resetForm();
      fetchLocations();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save location');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this location?')) return;
    try {
      await api.delete(`/locations/${id}/`);
      toast.success('Location deleted');
      fetchLocations();
    } catch { toast.error('Failed to delete location'); }
  };

  const resetForm = () => {
    setFormData({
      name: '', address: '', city: '', state: 'IL', zip_code: '',
      country: 'United States', country_code: 'US', timezone: 'America/Chicago',
      phone: '', email: '', capacity: 15, active: true,
      location_category: 'independent', eligibility: 'open', eligibility_detail: '',
      external_purchase: false, external_purchase_url: '',
    });
    setEditingLocation(null);
  };

  const openEditDialog = (location) => {
    setEditingLocation(location);
    setFormData({
      name: location.name,
      address: location.address,
      city: location.city,
      state: location.state,
      zip_code: location.zip_code,
      country: location.country || 'United States',
      country_code: location.country_code || 'US',
      timezone: location.timezone || 'America/Chicago',
      phone: location.phone || '',
      email: location.email || '',
      capacity: location.capacity,
      active: location.active,
      location_category: location.location_category || 'independent',
      eligibility: location.eligibility || 'open',
      eligibility_detail: location.eligibility_detail || '',
      external_purchase: location.external_purchase || false,
      external_purchase_url: location.external_purchase_url || '',
    });
    setDialogOpen(true);
  };

  const getCategoryConfig = (cat) => CATEGORY_OPTIONS.find(o => o.value === cat) || CATEGORY_OPTIONS[0];

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>;

  return (
    <div className="space-y-6" data-testid="admin-locations-page">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="font-heading text-2xl font-bold">Locations</h1>
          <p className="text-muted-foreground">Manage physical locations for courses and camps</p>
        </div>
        <Button onClick={() => { resetForm(); setDialogOpen(true); }} data-testid="add-location-btn">
          <Plus className="h-4 w-4 mr-1" /> Add Location
        </Button>
      </div>

      {/* Dialog */}
      <Dialog open={dialogOpen} onOpenChange={(open) => { if (!open) { setDialogOpen(false); resetForm(); } else setDialogOpen(true); }}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingLocation ? 'Edit Location' : 'Add Location'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label>Name *</Label>
              <Input value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} placeholder="Wheatlands Elementary School" required data-testid="location-name-input" />
            </div>

            {/* Location Category */}
            <div className="p-4 bg-blue-50 rounded-lg space-y-3">
              <Label className="font-semibold flex items-center gap-2">
                <Building2 className="h-4 w-4" /> Location Category
              </Label>
              <Select value={formData.location_category} onValueChange={(v) => setFormData({...formData, location_category: v})}>
                <SelectTrigger data-testid="location-category-select">
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent>
                  {CATEGORY_OPTIONS.map(opt => (
                    <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                {formData.location_category === 'school' && "School location — may have district enrollment restrictions"}
                {formData.location_category === 'park_district' && "Park district facility — typically open to district residents"}
                {formData.location_category === 'library' && "Public library — typically open to all community members"}
                {formData.location_category === 'independent' && "Independent facility — Ready Set STEM or partner space"}
              </p>
            </div>

            {/* Eligibility */}
            <div className="p-4 bg-amber-50 rounded-lg space-y-3">
              <Label className="font-semibold">Enrollment Eligibility</Label>
              <Select value={formData.eligibility} onValueChange={(v) => setFormData({...formData, eligibility: v})}>
                <SelectTrigger>
                  <SelectValue placeholder="Who can enroll?" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="open">Open to All</SelectItem>
                  <SelectItem value="district_only">School District Students Only</SelectItem>
                  <SelectItem value="members_only">Members Only</SelectItem>
                  <SelectItem value="partner_only">Partner Organization Only</SelectItem>
                </SelectContent>
              </Select>
              <Input
                value={formData.eligibility_detail}
                onChange={(e) => setFormData({...formData, eligibility_detail: e.target.value})}
                placeholder="e.g., SD 308 students only — school district requirement"
                className="text-sm"
              />
              <p className="text-xs text-muted-foreground">This restriction badge will appear next to the location on camp registration</p>
            </div>

            {/* External Purchase */}
            <div className="p-4 bg-purple-50 rounded-lg space-y-3">
              <div className="flex items-center justify-between">
                <Label className="font-semibold flex items-center gap-2">
                  <ExternalLink className="h-4 w-4" /> External Purchasing
                </Label>
                <Switch
                  checked={formData.external_purchase}
                  onCheckedChange={(v) => setFormData({...formData, external_purchase: v})}
                />
              </div>
              <p className="text-xs text-muted-foreground">
                Enable if registration/payment is handled on a partner website (e.g., park district portal)
              </p>
              {formData.external_purchase && (
                <div>
                  <Label className="text-sm">External Purchase URL *</Label>
                  <div className="flex items-center gap-2">
                    <Link2 className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                    <Input
                      value={formData.external_purchase_url}
                      onChange={(e) => setFormData({...formData, external_purchase_url: e.target.value})}
                      placeholder="https://parkdistrict.example.com/register"
                      type="url"
                      required={formData.external_purchase}
                    />
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">This URL replaces the "Add to Cart" button for sessions at this location</p>
                </div>
              )}
            </div>

            {/* Address */}
            <div>
              <Label>Address *</Label>
              <Input value={formData.address} onChange={(e) => setFormData({...formData, address: e.target.value})} placeholder="2600 Cranston Cir" required />
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label>City *</Label>
                <Input value={formData.city} onChange={(e) => setFormData({...formData, city: e.target.value})} placeholder="Aurora" required />
              </div>
              <div>
                <Label>State *</Label>
                <Input value={formData.state} onChange={(e) => setFormData({...formData, state: e.target.value})} placeholder="IL" required />
              </div>
              <div>
                <Label>ZIP *</Label>
                <Input value={formData.zip_code} onChange={(e) => setFormData({...formData, zip_code: e.target.value})} placeholder="60502" required />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Phone</Label>
                <Input value={formData.phone} onChange={(e) => setFormData({...formData, phone: e.target.value})} placeholder="(630) 555-0100" />
              </div>
              <div>
                <Label>Email</Label>
                <Input type="email" value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})} placeholder="location@readysetstem.com" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Capacity</Label>
                <Input type="number" value={formData.capacity} onChange={(e) => setFormData({...formData, capacity: parseInt(e.target.value)})} />
              </div>
              <div className="flex items-center gap-2 pt-6">
                <Switch checked={formData.active} onCheckedChange={(checked) => setFormData({...formData, active: checked})} />
                <Label>Active</Label>
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
              <Button type="submit" data-testid="save-location-btn">{editingLocation ? 'Update' : 'Create'}</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {CATEGORY_OPTIONS.map(cat => {
          const Icon = cat.icon;
          const count = locations.filter(l => (l.location_category || 'independent') === cat.value).length;
          return (
            <Card key={cat.value} className="card-dashboard">
              <CardContent className="p-4 text-center">
                <Icon className={`h-8 w-8 mx-auto mb-2 ${cat.color}`} />
                <p className="text-2xl font-bold">{count}</p>
                <p className="text-sm text-muted-foreground">{cat.label === 'Independent' ? 'Independent' : cat.label + 's'}</p>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Locations Table */}
      <Card className="card-dashboard">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Category</TableHead>
                <TableHead>Name</TableHead>
                <TableHead>Eligibility</TableHead>
                <TableHead>Address</TableHead>
                <TableHead>Contact</TableHead>
                <TableHead>Capacity</TableHead>
                <TableHead>Purchasing</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {locations.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} className="text-center py-8 text-muted-foreground">
                    No locations found. Add your first location above.
                  </TableCell>
                </TableRow>
              ) : (
                locations.map((location) => {
                  const cat = getCategoryConfig(location.location_category);
                  const CatIcon = cat.icon;
                  return (
                    <TableRow key={location.id} data-testid={`location-row-${location.id}`}>
                      <TableCell>
                        <div className="flex items-center gap-1.5">
                          <CatIcon className={`h-4 w-4 ${cat.color}`} />
                          <span className="text-xs font-medium">{cat.label}</span>
                        </div>
                      </TableCell>
                      <TableCell className="font-medium">{location.name}</TableCell>
                      <TableCell>
                        {location.eligibility === 'open' ? (
                          <Badge variant="secondary" className="bg-green-50 text-green-700 text-[10px]">Open to All</Badge>
                        ) : (
                          <Badge variant="outline" className="bg-amber-50 text-amber-700 text-[10px]">{location.eligibility_detail || 'Restricted'}</Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="text-sm">{location.address}</div>
                        <div className="text-xs text-muted-foreground">{location.city}, {location.state} {location.zip_code}</div>
                      </TableCell>
                      <TableCell>
                        {location.phone && <div className="flex items-center gap-1 text-xs"><Phone className="h-3 w-3" /> {location.phone}</div>}
                        {location.email && <div className="flex items-center gap-1 text-xs text-muted-foreground"><Mail className="h-3 w-3" /> {location.email}</div>}
                      </TableCell>
                      <TableCell>{location.capacity}</TableCell>
                      <TableCell>
                        {location.external_purchase ? (
                          <div className="flex items-center gap-1">
                            <Badge className="bg-purple-100 text-purple-700 text-[10px]"><ExternalLink className="h-3 w-3 mr-0.5" /> External</Badge>
                          </div>
                        ) : (
                          <span className="text-xs text-muted-foreground">In-App</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge variant={location.active ? 'default' : 'secondary'}>
                          {location.active ? 'Active' : 'Inactive'}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          <Button size="sm" variant="ghost" onClick={() => openEditDialog(location)} data-testid={`edit-location-${location.id}`}>
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => handleDelete(location.id)} data-testid={`delete-location-${location.id}`}>
                            <Trash2 className="h-4 w-4 text-red-500" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
